import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, MapPin, Eye, DollarSign, TrendingUp, Settings, Users, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { pickupAPI } from '../../services/api';
import { toast } from 'sonner';

const AdminPickupDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, posted: 0, active: 0, completed: 0 });
  const [allDeliveries, setAllDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Fee Configuration State
  const [feeConfig, setFeeConfig] = useState(null);
  const [feeMode, setFeeMode] = useState('percentage'); // 'percentage' or 'fixed'
  const [savingFees, setSavingFees] = useState(false);
  
  // Driver Applications State
  const [driverApplications, setDriverApplications] = useState([]);
  const [applicationFilter, setApplicationFilter] = useState('pending');
  const [loadingApplications, setLoadingApplications] = useState(false);

  // Runner Settings State
  const [runnerSettings, setRunnerSettings] = useState(null);
  const [savingRunner, setSavingRunner] = useState(false);

  useEffect(() => {
    loadStats();
    loadDeliveries();
    loadFeeConfig();
    loadDriverApplications();
    loadRunnerSettings();
  }, [filter, applicationFilter]);

  const loadRunnerSettings = async () => {
    try {
      const { data } = await pickupAPI.getRunnerSettings();
      setRunnerSettings(data.settings || {});
    } catch (error) {
      console.error('Load runner settings error:', error);
    }
  };

  const handleSaveRunnerSettings = async () => {
    setSavingRunner(true);
    try {
      await pickupAPI.updateRunnerSettings(runnerSettings);
      toast.success('Runner settings updated successfully');
      loadRunnerSettings();
    } catch (error) {
      console.error('Save runner settings error:', error);
      toast.error('Failed to update runner settings');
    } finally {
      setSavingRunner(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await pickupAPI.adminGetStats();
      setStats(data);
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const loadDeliveries = async () => {
    setLoading(true);
    try {
      const { data } = await pickupAPI.adminGetAllDeliveries(filter === 'all' ? null : filter);
      setAllDeliveries(data.deliveries || []);
    } catch (error) {
      console.error('Load deliveries error:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const loadFeeConfig = async () => {
    try {
      const { data } = await pickupAPI.getFeeConfig();
      setFeeConfig(data.config || {});
    } catch (error) {
      console.error('Load fee config error:', error);
    }
  };

  const loadDriverApplications = async () => {
    setLoadingApplications(true);
    try {
      const { data } = await pickupAPI.adminGetDriverApplications(
        applicationFilter === 'all' ? null : applicationFilter
      );
      setDriverApplications(data.applications || []);
    } catch (error) {
      console.error('Load applications error:', error);
      toast.error('Failed to load driver applications');
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleSaveFeeConfig = async () => {
    setSavingFees(true);
    try {
      await pickupAPI.updateFeeConfig(feeConfig);
      toast.success('Fee configuration updated successfully');
    } catch (error) {
      console.error('Save fee config error:', error);
      toast.error('Failed to update fee configuration');
    } finally {
      setSavingFees(false);
    }
  };

  const handleReviewApplication = async (applicationId, status, notes = '') => {
    try {
      await pickupAPI.adminReviewDriverApplication(applicationId, {
        application_id: applicationId,
        status,
        notes
      });
      toast.success(`Application ${status} successfully`);
      loadDriverApplications();
    } catch (error) {
      console.error('Review application error:', error);
      toast.error('Failed to review application');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      posted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pickup_confirmed: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      in_transit: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      delivered: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
      completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const DeliveryRow = ({ delivery }) => (
    <tr 
      className="hover:bg-muted/50 cursor-pointer"
      onClick={() => navigate(`/pickup/delivery/${delivery.id}`)}
    >
      <td className="px-4 py-3">
        <div className="text-sm">
          <div className="font-medium">{delivery.pickup_city} → {delivery.destination_city}</div>
          <div className="text-xs text-muted-foreground">
            {delivery.pickup_country} → {delivery.destination_country}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm">{delivery.customer_name}</td>
      <td className="px-4 py-3 text-sm">{delivery.driver_name || '-'}</td>
      <td className="px-4 py-3 text-sm">{delivery.package_type.replace('_', ' ')}</td>
      <td className="px-4 py-3 text-sm font-semibold">R{delivery.delivery_fee}</td>
      <td className="px-4 py-3">
        <Badge className={getStatusColor(delivery.status)}>
          {delivery.status.replace('_', ' ')}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {new Date(delivery.created_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <Button size="sm" variant="ghost">
          <Eye className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Matrise Runners Dashboard
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage errands, delivery and services marketplace
          </p>
        </div>

        {/* Stats */}
        <div className="grid gap-6 md:grid-cols-4">
          <StatCard title="Total Jobs" value={stats.total} icon={Package} color="bg-blue-500" />
          <StatCard title="Requested" value={stats.posted} icon={Clock} color="bg-yellow-500" />
          <StatCard title="Active" value={stats.active} icon={Truck} color="bg-green-500" />
          <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="bg-emerald-500" />
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="deliveries" className="space-y-4">
          <TabsList>
            <TabsTrigger value="deliveries">
              <Package className="h-4 w-4 mr-2" />
              All Jobs
            </TabsTrigger>
            <TabsTrigger value="commission">
              <DollarSign className="h-4 w-4 mr-2" />
              Commission
            </TabsTrigger>
            <TabsTrigger value="fees">
              <Settings className="h-4 w-4 mr-2" />
              Fee Configuration
            </TabsTrigger>
            <TabsTrigger value="drivers">
              <Users className="h-4 w-4 mr-2" />
              Runner Verification
            </TabsTrigger>
          </TabsList>

          {/* Runner Commission Tab */}
          <TabsContent value="commission" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Runner Commission Settings</CardTitle>
                <CardDescription>
                  Set the commission rate for Matrise Runners. This percentage is deducted from each job payment.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {runnerSettings && (
                  <>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label>Commission Rate (%)</Label>
                        <div className="flex items-center gap-4">
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={runnerSettings.commission_percentage || 12}
                            onChange={(e) => setRunnerSettings({
                              ...runnerSettings,
                              commission_percentage: parseFloat(e.target.value)
                            })}
                            className="max-w-xs"
                          />
                          <span className="text-muted-foreground">
                            Platform takes {runnerSettings.commission_percentage || 12}%, runner gets {100 - (runnerSettings.commission_percentage || 12)}%
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Example: On a R100 job, platform earns R{(runnerSettings.commission_percentage || 12).toFixed(0)}, 
                          runner earns R{(100 - (runnerSettings.commission_percentage || 12)).toFixed(0)}
                        </p>
                      </div>
                    </div>

                    <Button onClick={handleSaveRunnerSettings} disabled={savingRunner}>
                      {savingRunner ? 'Saving...' : 'Save Commission Settings'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deliveries Tab */}
          <TabsContent value="deliveries" className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Deliveries</CardTitle>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')}
                >
                  All
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'posted' ? 'default' : 'outline'}
                  onClick={() => setFilter('posted')}
                >
                  Posted
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'accepted' ? 'default' : 'outline'}
                  onClick={() => setFilter('accepted')}
                >
                  Active
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'completed' ? 'default' : 'outline'}
                  onClick={() => setFilter('completed')}
                >
                  Completed
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
              </div>
            ) : allDeliveries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No deliveries found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-medium">Route</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Driver</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Package</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDeliveries.map((delivery) => (
                      <DeliveryRow key={delivery.id} delivery={delivery} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Platform Metrics */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Fees Collected</span>
                  <span className="text-xl font-bold">
                    R{allDeliveries.filter(d => d.status === 'completed')
                      .reduce((sum, d) => sum + (d.platform_fee || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Runner Payouts</span>
                  <span className="text-xl font-bold">
                    R{allDeliveries.filter(d => d.status === 'completed')
                      .reduce((sum, d) => sum + (d.driver_payout || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avg. Job Fee</span>
                  <span className="text-xl font-bold">
                    R{allDeliveries.length > 0 
                      ? (allDeliveries.reduce((sum, d) => sum + d.delivery_fee, 0) / allDeliveries.length).toFixed(2)
                      : '0.00'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Cross-Border Jobs</span>
                  <span className="text-xl font-bold">
                    {allDeliveries.filter(d => d.pickup_country !== d.destination_country).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Domestic Jobs</span>
                  <span className="text-xl font-bold">
                    {allDeliveries.filter(d => d.pickup_country === d.destination_country).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Completion Rate</span>
                  <span className="text-xl font-bold">
                    {stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* Fee Configuration Tab */}
          <TabsContent value="fees">
            <Card>
              <CardHeader>
                <CardTitle>Platform Fee Configuration</CardTitle>
                <CardDescription>
                  Configure job fees for the Matrise Runners marketplace
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {feeConfig && (
                  <>
                    {/* Fee Mode Selector */}
                    <div className="space-y-2">
                      <Label>Fee Structure Mode</Label>
                      <div className="flex gap-4">
                        <Button
                          variant={feeMode === 'percentage' ? 'default' : 'outline'}
                          onClick={() => setFeeMode('percentage')}
                        >
                          Percentage-Based
                        </Button>
                        <Button
                          variant={feeMode === 'fixed' ? 'default' : 'outline'}
                          onClick={() => setFeeMode('fixed')}
                        >
                          Fixed Amount
                        </Button>
                      </div>
                    </div>

                    {/* Percentage-Based Fees */}
                    {feeMode === 'percentage' && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Global Platform Fee (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={feeConfig.global_platform_fee_percentage || 10}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              global_platform_fee_percentage: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Local Delivery Fee (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={feeConfig.local_delivery_fee_percentage || 8}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              local_delivery_fee_percentage: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Intercity Delivery Fee (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={feeConfig.intercity_delivery_fee_percentage || 10}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              intercity_delivery_fee_percentage: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cross-Border Delivery Fee (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={feeConfig.cross_border_delivery_fee_percentage || 12}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              cross_border_delivery_fee_percentage: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                      </div>
                    )}

                    {/* Fixed Amount Fees */}
                    {feeMode === 'fixed' && (
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Small Package Fee (R)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={feeConfig.small_package_fee || 5}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              small_package_fee: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Medium Package Fee (R)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={feeConfig.medium_package_fee || 10}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              medium_package_fee: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Large Package Fee (R)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={feeConfig.large_package_fee || 20}
                            onChange={(e) => setFeeConfig({
                              ...feeConfig,
                              large_package_fee: parseFloat(e.target.value)
                            })}
                          />
                        </div>
                      </div>
                    )}

                    {/* Common Settings */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Minimum Platform Fee (R)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={feeConfig.minimum_platform_fee || 5}
                          onChange={(e) => setFeeConfig({
                            ...feeConfig,
                            minimum_platform_fee: parseFloat(e.target.value)
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Insurance Fee (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={feeConfig.insurance_fee_percentage || 2}
                          onChange={(e) => setFeeConfig({
                            ...feeConfig,
                            insurance_fee_percentage: parseFloat(e.target.value)
                          })}
                        />
                      </div>
                    </div>

                    <Button onClick={handleSaveFeeConfig} disabled={savingFees}>
                      {savingFees ? 'Saving...' : 'Save Fee Configuration'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Driver Verification Tab */}
          <TabsContent value="drivers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Runner Applications & Verification</CardTitle>
                    <CardDescription>
                      Review and approve runner registrations
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={applicationFilter === 'pending' ? 'default' : 'outline'}
                      onClick={() => setApplicationFilter('pending')}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Pending
                    </Button>
                    <Button
                      size="sm"
                      variant={applicationFilter === 'approved' ? 'default' : 'outline'}
                      onClick={() => setApplicationFilter('approved')}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approved
                    </Button>
                    <Button
                      size="sm"
                      variant={applicationFilter === 'rejected' ? 'default' : 'outline'}
                      onClick={() => setApplicationFilter('rejected')}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Rejected
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingApplications ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                  </div>
                ) : driverApplications.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No runner applications found
                  </div>
                ) : (
                  <div className="space-y-4">
                    {driverApplications.map((app) => (
                      <Card key={app.id}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-3">
                                <h3 className="font-semibold text-lg">{app.full_name}</h3>
                                <Badge className={
                                  app.verification_status === 'verified'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                }>
                                  {app.verification_status}
                                </Badge>
                              </div>
                              <div className="grid md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                                <div>📧 {app.user_email}</div>
                                <div>📱 {app.phone}</div>
                                <div>🚗 {app.vehicle_type || 'N/A'}</div>
                                <div>📄 Documents: {app.documents_uploaded ? '✅ Uploaded' : '❌ Not uploaded'}</div>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Applied: {new Date(app.submitted_at).toLocaleDateString()}
                              </p>
                            </div>
                            
                            {app.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleReviewApplication(app.id, 'approved')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleReviewApplication(app.id, 'rejected', 'Did not meet requirements')}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPickupDashboard;
